import { ETelemetryTopOf } from 'multirpc-sdk';
import { useMemo } from 'react';

import { REFETCH_STATS_INTERVAL } from 'modules/stats/const';
import { getStatsChainID } from 'modules/chains/utils/getStatsChainID';
import {
  selectMappedPrivateRequestsChartData,
  selectMappedPrivateTrafficChartData,
  selectPrivateCountriesCountsByTrafficMap,
  selectPrivateCountriesCountsMap,
  selectPrivateTotalBytes,
  selectPrivateTotalRequests,
} from 'modules/stats/store/private/selectors';
import { useAppSelector } from 'store/useAppSelector';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useChainSelectors } from 'modules/chains/components/ChainSelectorsProvider';
import { useFetchPrivateStatsQuery } from 'modules/stats/actions/fetchPrivateStats';
import { usePrivateStatsParams } from 'modules/chains/hooks/usePrivateStatsParams';
import { useTimeframe } from 'modules/chains/components/TimeframeProvider';

import { StatsContextValue } from '../context';

const metrics = [ETelemetryTopOf.COUNTRY, ETelemetryTopOf.COUNTRY_BY_TRAFFIC];

export const usePrivateStats = () => {
  const {
    chain,
    chainCallProtocol,
    chainSubType,
    chainType,
    consensus,
    group,
    isConsensusEnabled,
  } = useChainSelectors();

  const { timeframe } = useTimeframe();

  const chainId = getStatsChainID({
    chainCallProtocol,
    chainSubType,
    chainType,
    consensus,
    group,
    isConsensusEnabled,
    publicChain: chain,
  });

  const { privateStatsParams } = usePrivateStatsParams({
    chainId,
    includeTrafficTimeline: true,
    metrics,
    timeframe,
  });

  const { isLoggedIn } = useAuth();

  const { isFetching: loading } = useFetchPrivateStatsQuery(
    privateStatsParams,
    {
      refetchOnMountOrArgChange: REFETCH_STATS_INTERVAL,
      skip: !isLoggedIn,
    },
  );

  const countries = useAppSelector(state =>
    selectPrivateCountriesCountsMap(state, privateStatsParams),
  );
  const countriesByTraffic = useAppSelector(state =>
    selectPrivateCountriesCountsByTrafficMap(state, privateStatsParams),
  );
  const requestsChartData = useAppSelector(state =>
    selectMappedPrivateRequestsChartData(state, privateStatsParams),
  );
  const trafficChartData = useAppSelector(state =>
    selectMappedPrivateTrafficChartData(state, privateStatsParams),
  );
  const totalBytes = useAppSelector(state =>
    selectPrivateTotalBytes(state, privateStatsParams),
  );
  const totalRequests = useAppSelector(state =>
    selectPrivateTotalRequests(state, privateStatsParams),
  );

  return useMemo(
    (): StatsContextValue => ({
      countries,
      countriesByTraffic,
      loading,
      requestsChartData,
      totalBytes,
      totalRequests,
      trafficChartData,
    }),
    [
      countries,
      countriesByTraffic,
      loading,
      requestsChartData,
      totalBytes,
      totalRequests,
      trafficChartData,
    ],
  );
};
